import { h, Fragment } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import register from "preact-custom-element";
import getShippingDetails from "./getShippingDetails";
import { ZIP_CODE_KEY } from "./estimatedDeliveryStorageKeys";

const saveZip = (zip) => {
  try {
    window.localStorage.setItem(ZIP_CODE_KEY, zip);
  } catch (error) {
    console.warn(error);
  }
};

const getZip = () => {
  try {
    return window.localStorage.getItem(ZIP_CODE_KEY);
  } catch (error) {
    console.warn(error);
    return null;
  }
};

const isValidZip = (zip) => /\d{5}/.test(zip);

function EstimatedDelivery({ initialVariantId, initialZip }) {
  const [loading, setLoading] = useState(false);
  const [variantId, setVariantId] = useState(initialVariantId);
  const [zip, setZip] = useState(getZip() || initialZip);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onUpdated = (e) => {
      const { variant } = e.detail;

      if (variant.quantityAvailable > 0) {
        setVariantId(variant.id.replace("gid://shopify/ProductVariant/", ""));
      } else {
        setVariantId(null);
      }
      setDeliveryDate(null);
    };

    document.addEventListener("productUpdate", onUpdated);

    return () => document.removeEventListener("productUpdate", onUpdated);
  }, []);

  const getDeliveryDate = async () => {
    setLoading(true);
    setError(null);
    const rates = await getShippingDetails({ zip, variantId });

    const date = rates?.shipping_rates?.[0]?.delivery_date;
    console.log(`rates`, rates);
    if (!date) {
      if (rates?.zip?.length) {
        setError("Please enter a valid zip code. ");
      } else {
        setError("Unable to estimate delivery at this time.");
      }
      setDeliveryDate(null);
      setLoading(false);
      return;
    }

    const offset = new Date().getTimezoneOffset() * 60 * 1000;
    const dateValue = new Date(date).valueOf();

    setDeliveryDate(
      new Date(dateValue + offset).toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    );
    setLoading(false);
  };

  if (!variantId) return null;

  return (
    <div className="estimated-delivery">
      <div className="estimated-delivery__title">Estimated Delivery</div>
      {loading && (
        <div className="estimated-delivery__loading">
          <div className="estimated-delivery__loading-line estimated-delivery__loading-line--line-1">
            <span className="visually-hidden">Loading</span>
          </div>
          <div className="estimated-delivery__loading-line estimated-delivery__loading-line--line-2"></div>
        </div>
      )}
      {!deliveryDate && !loading && (
        <>
          <div className="estimated-delivery__form">
            <div className="estimated-delivery__input-wrapper">
              <label className="estimated-delivery__label">
                <span className="visually-hidden">Zip code</span>
                <input
                  type="text"
                  className="estimated-delivery__input"
                  placeholder="Enter ZIP Code"
                  required
                  pattern="\d{5}"
                  onChange={(e) => {
                    saveZip(e.target.value);
                    setZip(e.target.value);

                    if (error && isValidZip(e.target.value)) {
                      setError(null);
                    }
                  }}
                  onFocusOut={() => {
                    const error = isValidZip(zip)
                      ? ""
                      : "Please enter a valid zip code.";
                    setError(error);
                  }}
                  value={zip}
                />
              </label>
              <button
                type="button"
                className="estimated-delivery__submit"
                onClick={() => getDeliveryDate()}
                disabled={!isValidZip(zip)}
              >
                Submit
              </button>
            </div>
            {error && <div className="estimated-delivery__error">{error}</div>}
          </div>
          <button
            type="button"
            data-modal-trigger="estimated-delivery"
            className="estimated-delivery__disclaimer-link"
          >
            Exclusions Apply
          </button>
        </>
      )}
      {!error && !loading && deliveryDate && (
        <>
          <div className="estimated-delivery__delivery">
            <span>{`${deliveryDate} to `}</span>
            <button
              type="button"
              className="estimated-delivery__clear"
              onClick={() => setDeliveryDate(null)}
            >
              {zip}
            </button>
          </div>
          <div className="estimated-delivery__delivery-disclaimer">
            Delays may occur. Arrival date not guaranteed.{" "}
            <button
              type="button"
              data-modal-trigger="estimated-delivery"
              class="estimated-delivery__delivery-disclaimer-button"
            >
              See details.
            </button>
          </div>
        </>
      )}
    </div>
  );
}

if (!customElements.get("estimated-delivery")) {
  register(EstimatedDelivery, "estimated-delivery", ["initial-variant-id"], {
    shadow: false,
  });
}

// Save all cart items (qty, properties, variant ID)
// Clear cart
// Add current variant
// Get shipping rates
// Clear cart
// Re-add all items
